import { useCallback, useState } from 'react'
import { Box, Flex } from '@mantine/core'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'

import type { Chart, ChartsData, List, Users } from '@/types/pdfExport.types'

import { AgeSummaryShell } from '@/modules/employer/pages/components/AgeSummaryShell/AgeSummaryShell'
import { GenderSummaryShell } from '@/modules/employer/pages/components/GenderSummaryShell/GenderSummaryShell'
import { IncomeSummaryShell } from '@/modules/employer/pages/components/IncomeSummaryShell/IncomeSummaryShell'
import { RaceSummaryShell } from '@/modules/employer/pages/components/RaceSummaryShell/RaceSummaryShell'
import { TopicsSummaryShell } from '@/modules/employer/pages/components/TopicsSummaryShell/TopicsSummaryShell'
import { UsersSummaryShell } from '@/modules/employer/pages/components/UsersSummaryShell/UsersSummaryShell'

import { Button } from '@/components/Button/Button'
import { StatsExport } from '@/components/StatsExport/StatsExport'
import { generatePdf } from '@/components/StatsExport/StatsExport.config'

export function DashboardShell() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [chartsData, setChartsData] = useState<ChartsData>({
    age: undefined,
    gender: undefined,
    income: undefined,
    race: undefined,
    topics: undefined,
    users: undefined,
  })

  const isMutating = useIsMutating()
  const isFetching = useIsFetching()

  const handleExportToPDF = () => {
    setIsGenerating(true)

    generatePdf(<StatsExport chartsData={chartsData} />, setIsGenerating)
  }

  const handleUpdateChartsData = useCallback(
    (data: Users | List[] | Chart, key: string) => {
      setChartsData(prev => ({ ...prev, [key]: data }))
    },
    [],
  )

  return (
    <Box maw="1600px" mx="auto">
      <Flex
        align="center"
        direction={{
          base: 'column',
          sm: 'row',
        }}
        justify="flex-end"
        mb={24}
      >
        <Button
          disabled={!!isFetching || !!isMutating}
          h={38}
          loading={isGenerating}
          miw={100}
          onClick={handleExportToPDF}
          size="md"
          w={{
            base: '100%',
            sm: 'auto',
          }}
        >
          Export to PDF
        </Button>
      </Flex>
      <Box>
        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
        >
          <UsersSummaryShell onUpdateChartsData={handleUpdateChartsData} />

          <GenderSummaryShell onUpdateChartsData={handleUpdateChartsData} />
        </Flex>

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
          mt={24}
        >
          <AgeSummaryShell onUpdateChartsData={handleUpdateChartsData} />

          <IncomeSummaryShell onUpdateChartsData={handleUpdateChartsData} />
        </Flex>

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap={24}
          mt={24}
        >
          <RaceSummaryShell onUpdateChartsData={handleUpdateChartsData} />

          <TopicsSummaryShell onUpdateChartsData={handleUpdateChartsData} />
        </Flex>
      </Box>
    </Box>
  )
}
